import React, { useEffect, useState } from "react";
import { TvStats } from "./TvStats";
import { TvChart } from "./TvChart";
import { RothCard } from "./RothCard";
import { TvPositions, KalshiMini, TvTicker } from "./TvSide";
import { FloorTvAvV3, FloorTvHercules2, useFloorTrading } from "./FloorTvTrading";
import { EngineV2Board, useEngineV2 } from "./FloorTvV2";
import { HtuNumbers, HtuProgress, useHtu } from "../tv/HtuSlide";
import { OakDeals, OakGrowth, OakSales, useOakton } from "../tv/OaktonSlide";
import { tvControlGet } from "../../lib/api";

const v2Ticker = (v2, side) => {
  const news = (side?.ticker || []).filter((x) => x.k === "news");
  const ent = (v2?.log || []).filter((l) => l.action === "entry").slice(0, 8)
    .map((l) => ({ agent: "V2 FILL", text: `${l.ticker} $${Math.round(l.filled_usd || 0)} · SL ${l.stop} · TP ${l.target}` }));
  const scan = (v2?.last_scan?.candidates || []).filter((c) => c.holding && !c.blocked_by?.length).slice(0, 10)
    .map((c) => ({ agent: c.direction === "long" ? "V2 LONG" : "V2 SHORT", text: `${c.ticker} — ${c.trigger}` }));
  return [...ent, ...scan, ...news];
};
import { TvBrokers } from "./TvBrokers";
import { useFloorSide, useFloorOps } from "./useHouseLive";

const SCREENS = ["Floor · Positions · Kalshi", "Engine V2 · Live Scan", "AV Trading v3", "Hercules 2.0", "HTU · Performance", "HTU · Insights", "Oakton · Sales", "Oakton · Growth", "Oakton · Deals"];
const TABS = ["Floor", "Engine V2", "AV v3", "Hercules 2.0", "HTU", "HTU Insights", "Oakton", "Oakton Growth", "Oakton Deals"];
const REMOTE_IDS = ["floor", "v2", "av-v3", "hercules", "htu", "htu-progress", "oakton", "oakton-growth", "oakton-deals"];

const useFloorRemote = (uid, setScr) => {
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (!uid) return undefined;
    let v = -1;
    const poll = () => tvControlGet(uid).then((c) => {
      if (c.v === v) return;
      v = c.v;
      if (c.screen !== "floor") return;
      const k = REMOTE_IDS.indexOf(c.scene);
      if (k >= 0) setScr(k);
      setPaused(!!c.paused);
    }).catch(() => {});
    poll(); const t = setInterval(poll, 1500); return () => clearInterval(t);
  }, [uid, setScr]);
  return paused;
};

export const FloorTvDashboard = ({ d, equity, gates, hidden, uid }) => {
  const side = useFloorSide();
  const ops = useFloorOps();
  const v2 = useEngineV2();
  const [scr, setScr] = useState(0);
  const paused = useFloorRemote(uid, setScr);
  const htu = useHtu();
  const oak = useOakton();
  const trading = useFloorTrading();
  const full = scr >= 2;
  useEffect(() => { if (paused) return undefined; const t = setInterval(() => setScr((x) => (x + 1) % SCREENS.length), 30000); return () => clearInterval(t); }, [paused]);
  return (
    <div className="fc-room fx-tv" data-testid="floor-command-room" data-screen={scr} style={full ? { gridTemplateRows: "minmax(0, 1fr) 1.6em", gridTemplateColumns: "minmax(0, 1fr)" } : undefined}>
      <div className="fx-sweep" />
      {!full && <div className="flex flex-col gap-[0.4em]">
        <TvStats pnl={d.pnl} equity={equity} live={d.live} gates={gates} hidden={hidden} />
        <TvBrokers brokers={ops?.brokers} hidden={hidden} />
      </div>}
      {scr === 0 ? (
        <div className="min-h-0 grid gap-[0.55em]" style={{ gridTemplateColumns: "minmax(0,66fr) minmax(0,34fr)" }} data-testid="tv-floor-screen">
          <TvChart positions={d.positions} hidden={hidden} />
          <div className="min-h-0 flex flex-col gap-[0.55em]">
            <div className="flex-1 min-h-0 flex flex-col"><RothCard /></div>
            <div className="min-h-0 max-h-[55%] flex flex-col"><TvPositions positions={d.positions} hidden={hidden} equity={equity} /></div>
            <KalshiMini k={side?.kalshi} hidden={hidden} />
          </div>
        </div>
      ) : scr === 1 ? <div className="min-h-0" data-testid="tv-v2-screen"><EngineV2Board d={v2} /></div>
        : scr === 2 ? <FloorTvAvV3 d={trading} />
        : scr === 3 ? <FloorTvHercules2 d={trading} />
        : <div className="min-h-0" data-testid="tv-htu-screen">{scr === 4 ? <HtuNumbers d={htu} /> : scr === 5 ? <HtuProgress d={htu} />
          : scr === 6 ? <OakSales d={oak} /> : scr === 7 ? <OakGrowth d={oak} /> : <OakDeals d={oak} />}</div>}
      {!full && <TvTicker items={v2Ticker(v2, side)} hidden={hidden} />}
      <footer className="flex items-center justify-center gap-[1.2em] fc-label min-w-0 overflow-hidden" data-testid="floor-footer">
        <span className="whitespace-nowrap">POWERED BY <span className="text-[#00F0FF]">A V TECH</span></span>
        <span className="flex items-center gap-[0.35em] min-w-0 overflow-hidden" data-testid="tv-screen-indicator">
          {SCREENS.map((s, i) => (
            <button key={s} onClick={() => setScr(i)} className={`fc-num text-[0.85em] whitespace-nowrap px-[0.55em] rounded-full border ${i === scr ? "border-[#00F0FF] bg-[#00F0FF] text-[#030712] font-bold" : "border-cyan-300/30 text-cyan-200/60"}`} data-testid={`tv-screen-${i}`}>{i + 1} · {TABS[i]}</button>
          ))}
        </span>
      </footer>
    </div>
  );
};
